/* eslint-disable no-underscore-dangle */
import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Stack,
  Typography,
  IconButton,
  Paper,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { useMutation, useQuery } from '@apollo/client'
import mimeTypes from 'mime'
import _get from 'lodash/get'
import axios from 'axios'
import checkedBibMutation from '../../graphql/mutation/checkedBib'
import eventByIdQuery from '../../graphql/query/eventById'
import { useToast } from '../../contexts/ToastContext'

const AddParticipantModal = ({ open, onClose, eventId, onSuccess }) => {
  const { showSuccess, showError } = useToast()
  const [bib, setBib] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [gender, setGender] = useState('')
  const [ageCategory, setAgeCategory] = useState('')
  const [row, setRow] = useState('')
  const [nationality, setNationality] = useState('')
  const [nationalId, setNationalId] = useState('')
  const [shirtSize, setShirtSize] = useState('')
  const [shirtType, setShirtType] = useState('')
  const [ticketCode, setTicketCode] = useState('')
  const [ticketType, setTicketType] = useState('')
  const [file, setFile] = useState('')
  const [fileImg, setFileImg] = useState(null)
  const [filePreview, setFilePreview] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const { data: eventData } = useQuery(eventByIdQuery, {
    variables: {
      _id: eventId,
    },
    skip: !eventId,
  })

  const [checkedBib] = useMutation(checkedBibMutation)

  const isFaceCheckin = _get(eventData, 'eventId.isFaceCheckin', false)
  const requireTicketCode = _get(eventData, 'eventId.options.search.qrcode', false)

  const genderList = ['male', 'female']

  const handleClose = () => {
    // Reset form
    setBib('')
    setFirstName('')
    setLastName('')
    setDisplayName('')
    setBirthDate('')
    setGender('')
    setAgeCategory('')
    setRow('')
    setNationality('')
    setNationalId('')
    setShirtSize('')
    setShirtType('')
    setTicketCode('')
    setTicketType('')
    setFile('')
    setFileImg(null)
    setFilePreview(null)
    setSubmitting(false)
    onClose()
  }

  const selectFile = async (e) => {
    e.preventDefault()
    const tempFile = e.currentTarget.files[0]
    if (!tempFile) return

    setFileImg(tempFile)

    // Create preview
    const reader = new FileReader()
    reader.onload = (event) => {
      setFilePreview(event.target.result)
    }
    reader.readAsDataURL(tempFile)

    // Upload file
    try {
      const extType = mimeTypes.getExtension(tempFile.type)
      const ext = mimeTypes.getType(extType)
      const response = await fetch(process.env.REACT_APP_UPLOAD_SERVER, {
        method: 'POST',
        body: JSON.stringify({
          ext: `${ext}`,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const result = await response.json()
      await axios.put(result.uploadUri, tempFile, {
        headers: {
          'Content-Type': `${ext}`,
          'x-goog-acl': 'public-read',
        },
      })
      setFile(result.downloadUri)
    } catch (error) {
      console.error('Error uploading file:', error)
      showError('Failed to upload image. Please try again.')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      const record = {
        bib: `${bib}`,
        firstname: firstName,
        lastname: lastName,
        displayName: displayName,
        birthDate: birthDate,
        ...(isFaceCheckin && {
          photo: file,
        }),
        gender: gender,
        ageCategory: ageCategory,
        row: row,
        nationality: nationality,
        nationalId: `${nationalId}`,
        shirt: { size: shirtSize, type: shirtType },
        // Always send `options` — the mobile app dereferences it without a
        // null check, and it reads the birth date out of there.
        options: {
          ...(ticketCode && { ticketCode }),
          ...(ticketType && { ticketType }),
          ...(birthDate && { birthDate }),
        },
        eventId: eventId,
      }

      const res = await checkedBib({
        variables: {
          filter: record,
        },
      })

      if (res.data.checkedBib === null) {
        showError('Bib already exists!', 'Duplicate Bib')
        setSubmitting(false)
      } else {
        showSuccess('Participant created successfully!', 'Success')
        setTimeout(() => {
          handleClose()
          if (onSuccess) {
            onSuccess()
          }
        }, 1500)
      }
    } catch (error) {
      console.error('Error creating participant:', error)
      showError('Failed to create participant. Please try again.')
      setSubmitting(false)
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            maxHeight: { xs: '95vh', sm: '90vh', md: '85vh' },
          },
        }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: { xs: 1.5, sm: 2, md: 2.5 },
            borderBottom: (theme) =>
              `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
          }}>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
            }}>
            Add Participant
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'rgba(0, 0, 0, 0.04)'
                    : 'rgba(255, 255, 255, 0.08)',
              },
            }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            '&.MuiDialogContent-root': {
              paddingTop: { xs: 2, sm: 2.5, md: 3 },
            },
          }}>
          <form onSubmit={handleSubmit} id="add-participant-form">
            <Stack spacing={{ xs: 2.5, sm: 3, md: 3.5 }}>
              {/* Detail Section */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                    mb: { xs: 1.5, sm: 2, md: 2.5 },
                  }}>
                  Detail
                </Typography>
                <Stack spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
                  {/* Bib */}
                  <TextField
                    label="Bib"
                    value={bib}
                    onChange={(e) => setBib(e.target.value)}
                    required
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                        borderRadius: 1,
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(255, 255, 255, 1)'
                            : 'rgba(18, 18, 18, 1)',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                      },
                    }}
                  />

                  {/* First Name and Last Name */}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
                    <TextField
                      label="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                          borderRadius: 1,
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(255, 255, 255, 1)'
                              : 'rgba(18, 18, 18, 1)',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                        },
                      }}
                    />
                    <TextField
                      label="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                          borderRadius: 1,
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(255, 255, 255, 1)'
                              : 'rgba(18, 18, 18, 1)',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                        },
                      }}
                    />
                  </Stack>

                  {/* Display Name and Birth Date */}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
                    <TextField
                      label="Display Name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      required
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                          borderRadius: 1,
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(255, 255, 255, 1)'
                              : 'rgba(18, 18, 18, 1)',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                        },
                      }}
                    />
                    <TextField
                      label="Birth Date"
                      type="date"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                      required
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                          borderRadius: 1,
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(255, 255, 255, 1)'
                              : 'rgba(18, 18, 18, 1)',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                        },
                      }}
                    />
                  </Stack>

                  {/* Photo Upload (if isFaceCheckin) */}
                  {isFaceCheckin && (
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: 'text.secondary',
                          fontSize: {
                            xs: '0.75rem',
                            sm: '0.8125rem',
                            md: '0.875rem',
                          },
                          mb: { xs: 0.75, sm: 1, md: 1.25 },
                        }}>
                        Photo{' '}
                        <Box component="span" sx={{ color: 'error.main' }}>
                          *
                        </Box>
                      </Typography>
                      <Paper
                        elevation={0}
                        sx={{
                          border: (theme) =>
                            `2px dashed ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)'}`,
                          borderRadius: 1,
                          p: { xs: 1.5, sm: 2, md: 2.5 },
                          textAlign: 'center',
                          cursor: 'pointer',
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(0, 0, 0, 0.02)'
                              : 'rgba(255, 255, 255, 0.03)',
                          '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: (theme) =>
                              theme.palette.mode === 'light'
                                ? 'rgba(0, 0, 0, 0.04)'
                                : 'rgba(255, 255, 255, 0.05)',
                          },
                          transition: (theme) =>
                            theme.transitions.create(['border-color', 'background-color'], {
                              duration: theme.transitions.duration.short,
                            }),
                        }}
                        onClick={() => document.getElementById('photo-upload-input').click()}>
                        <input
                          id="photo-upload-input"
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={selectFile}
                        />
                        {filePreview ? (
                          <Box>
                            <Box
                              component="img"
                              src={filePreview}
                              alt="Preview"
                              sx={{
                                maxWidth: '100%',
                                maxHeight: { xs: 150, sm: 200, md: 250 },
                                borderRadius: 1,
                                mb: { xs: 1, sm: 1.25, md: 1.5 },
                              }}
                            />
                            <Typography
                              sx={{
                                color: 'text.secondary',
                                fontSize: {
                                  xs: '0.8125rem',
                                  sm: '0.875rem',
                                  md: '0.9375rem',
                                },
                              }}>
                              Click to change image
                            </Typography>
                          </Box>
                        ) : (
                          <>
                            <CloudUploadIcon
                              sx={{
                                fontSize: {
                                  xs: '2.5rem',
                                  sm: '3rem',
                                  md: '3.5rem',
                                },
                                color: 'primary.main',
                                mb: { xs: 1, sm: 1.25, md: 1.5 },
                                opacity: 0.8,
                              }}
                            />
                            <Typography
                              sx={{
                                color: 'primary.main',
                                fontWeight: 600,
                                fontSize: {
                                  xs: '0.8125rem',
                                  sm: '0.875rem',
                                  md: '0.9375rem',
                                },
                                mb: { xs: 0.5, sm: 0.75, md: 1 },
                              }}>
                              Click to upload or <strong>drag and drop</strong>
                            </Typography>
                            <Typography
                              sx={{
                                color: 'text.secondary',
                                fontSize: {
                                  xs: '0.75rem',
                                  sm: '0.8125rem',
                                  md: '0.875rem',
                                },
                              }}>
                              {fileImg ? fileImg.name : 'Upload participant photo'}
                            </Typography>
                          </>
                        )}
                      </Paper>
                    </Box>
                  )}

                  {/* Gender and Age Category */}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
                    <FormControl
                      required
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                          borderRadius: 1,
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(255, 255, 255, 1)'
                              : 'rgba(18, 18, 18, 1)',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                        },
                      }}>
                      <InputLabel>Gender</InputLabel>
                      <Select
                        value={gender}
                        onChange={(e) => setGender(e.target.value)}
                        label="Gender">
                        {genderList.map((g) => (
                          <MenuItem key={g} value={g}>
                            {g.charAt(0).toUpperCase() + g.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Age Category"
                      value={ageCategory}
                      onChange={(e) => setAgeCategory(e.target.value)}
                      required
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                          borderRadius: 1,
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(255, 255, 255, 1)'
                              : 'rgba(18, 18, 18, 1)',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                        },
                      }}
                    />
                  </Stack>

                  {/* Row, Nationality, National ID */}
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
                    <TextField
                      label="Row"
                      value={row}
                      onChange={(e) => setRow(e.target.value)}
                      required
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                          borderRadius: 1,
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(255, 255, 255, 1)'
                              : 'rgba(18, 18, 18, 1)',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                        },
                      }}
                    />
                    <TextField
                      label="Nationality"
                      value={nationality}
                      onChange={(e) => setNationality(e.target.value)}
                      required
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                          borderRadius: 1,
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(255, 255, 255, 1)'
                              : 'rgba(18, 18, 18, 1)',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                        },
                      }}
                    />
                    <TextField
                      label="National ID"
                      value={nationalId}
                      onChange={(e) => setNationalId(e.target.value)}
                      required
                      fullWidth
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                          borderRadius: 1,
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(255, 255, 255, 1)'
                              : 'rgba(18, 18, 18, 1)',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                        },
                      }}
                    />
                  </Stack>
                </Stack>
              </Box>

              {/* Shirt Detail Section */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                    mb: { xs: 1.5, sm: 2, md: 2.5 },
                  }}>
                  Shirt Detail
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
                  <TextField
                    label="Shirt Size"
                    value={shirtSize}
                    onChange={(e) => setShirtSize(e.target.value)}
                    required
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                        borderRadius: 1,
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(255, 255, 255, 1)'
                            : 'rgba(18, 18, 18, 1)',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                      },
                    }}
                  />
                  <TextField
                    label="Shirt Type"
                    value={shirtType}
                    onChange={(e) => setShirtType(e.target.value)}
                    required
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                        borderRadius: 1,
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(255, 255, 255, 1)'
                            : 'rgba(18, 18, 18, 1)',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                      },
                    }}
                  />
                </Stack>
              </Box>

              {/* Ticket Section */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                    mb: { xs: 1.5, sm: 2, md: 2.5 },
                  }}>
                  Ticket
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
                  <TextField
                    label="Ticket Code"
                    value={ticketCode}
                    onChange={(e) => setTicketCode(e.target.value)}
                    required={requireTicketCode}
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                        borderRadius: 1,
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(255, 255, 255, 1)'
                            : 'rgba(18, 18, 18, 1)',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                      },
                    }}
                  />
                  <TextField
                    label="Ticket Type"
                    value={ticketType}
                    onChange={(e) => setTicketType(e.target.value)}
                    required
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                        borderRadius: 1,
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(255, 255, 255, 1)'
                            : 'rgba(18, 18, 18, 1)',
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                      },
                    }}
                  />
                </Stack>
              </Box>
            </Stack>
          </form>
        </DialogContent>

        <DialogActions
          sx={{
            p: { xs: 1.5, sm: 2, md: 2.5 },
            borderTop: (theme) =>
              `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
            justifyContent: 'flex-end',
            gap: { xs: 1, sm: 1.25, md: 1.5 },
          }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={submitting}
            sx={{
              borderRadius: 1,
              fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
              fontWeight: 500,
              textTransform: 'none',
              px: { xs: 1.5, sm: 2, md: 2.5 },
              py: { xs: 1, sm: 1.125, md: 1.25 },
            }}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="add-participant-form"
            variant="contained"
            color="primary"
            disabled={submitting}
            sx={{
              borderRadius: 1,
              fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
              fontWeight: 600,
              textTransform: 'none',
              px: { xs: 1.5, sm: 2, md: 2.5 },
              py: { xs: 1, sm: 1.125, md: 1.25 },
            }}>
            {submitting ? 'Creating...' : 'Create Participant'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AddParticipantModal
